import { ErrorOriginEnum } from "@/enums";
import type { ClassifyResult, ErrorSource, ErrorTagsRecord } from "@/types";
import {
  isFunctionsHttpError,
  isPostgrestError,
  isSupabaseAuthError,
} from "../supabase";

// Rate limit TTL in milliseconds
export const RATE_LIMIT_MS = 60_000;

const lastSentAt = new Map<string, number>();
export function shouldRateLimit(
  key: string,
  ttlMs: number = RATE_LIMIT_MS,
): boolean {
  const prev = lastSentAt.get(key) ?? 0;
  const now = Date.now();
  if (now - prev < ttlMs) return true;
  lastSentAt.set(key, now);
  return false;
}

// Drop-list for expected/business 4xx statuses
export const DROP_4XX = new Set<number>([401, 403, 404, 409, 422]);

export function getHttpStatus(error: unknown): number | undefined {
  const candidate = error as {
    status?: unknown;
    response?: { status?: unknown };
  };
  const status = (candidate?.response?.status ?? candidate?.status) as unknown;
  return typeof status === "number" ? status : undefined;
}

export function isAbortOrCancel(error: unknown): boolean {
  const msg = String(
    (error as { message?: string })?.message || "",
  ).toLowerCase();
  return (
    msg.includes("abort") ||
    msg.includes("canceled") ||
    msg.includes("cancelled")
  );
}

export function isOfflineOrNetwork(error: unknown): boolean {
  const msg = String(
    (error as { message?: string })?.message || "",
  ).toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("network error")
  );
}

export function detectOrigin(error: unknown): ErrorOriginEnum {
  const name = (error as { name?: string })?.name || "";

  if (isFunctionsHttpError(error)) return ErrorOriginEnum.SUPABASE;
  if (name === "FunctionsRelayError" || name === "FunctionsFetchError")
    return ErrorOriginEnum.HTTP;

  if (isSupabaseAuthError(error) || isPostgrestError(error))
    return ErrorOriginEnum.SUPABASE;

  if (isOfflineOrNetwork(error)) return ErrorOriginEnum.HTTP;

  return ErrorOriginEnum.JS;
}

// Build standard error tags; source is 'react-query' | 'supabase'
export function buildErrorTagsBase(
  source: ErrorSource,
  scope: string,
  verdict: ClassifyResult,
  extras?: { endpoint?: string; method?: string; code?: string },
): ErrorTagsRecord {
  return {
    source,
    scope,
    ...(verdict.status ? { status: String(verdict.status) } : {}),
    ...(extras?.endpoint ? { endpoint: extras.endpoint } : {}),
    ...(extras?.method ? { method: extras.method } : {}),
    ...(extras?.code ? { code: extras.code } : {}),
  };
}

// Compose reusable prefix for rate-limit keys
export function composeRateLimitPrefix(
  verdict: ClassifyResult,
  error: unknown,
): string {
  if (isOfflineOrNetwork(error)) return "offline";
  const code = (error as { code?: unknown })?.code;
  const name = (error as { name?: string })?.name || "";
  const status = verdict.status ?? "no-status";
  return `${status}:${name}:${String(code ?? "")}`;
}
